// src/pages/fiscal/mdfe/NFeAutorizadaSearch.js
import React, { useEffect, useMemo, useState } from 'react';

import Input from '../../../components/input/Input';
import ModalBody from '../../../components/modalForm/ModalBody';
import ModalContainer from '../../../components/modalForm/ModalContainer';
import ModalFooter from '../../../components/modalForm/ModalFooter';
import ModalHeader from '../../../components/modalForm/ModalHeader';
import ModalTable from '../../../components/modalForm/ModalTable';
import LazyLoading from '../../../components/modalForm/LazyLoading';

import { normalizeText } from '../../../utils/stringUtils';
import { ModalActionButton } from '../../../components/modalForm/ModalActions';
import ModalSelectButton from '../../../components/modalForm/ModalSelectButton';
import ModalSearchBar from '../../../components/modalForm/ModalSearchBar';

const formatarDataInput = (data) => {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
};
const primeiroDiaMes = () => {
  const agora = new Date();
  return formatarDataInput(new Date(agora.getFullYear(), agora.getMonth(), 1));
};
const ultimoDiaMes = () => {
  const agora = new Date();
  return formatarDataInput(new Date(agora.getFullYear(), agora.getMonth() + 1, 0));
};
const moeda = (valor) => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const obterDataNota = (nota) => String(nota.dataEmissao || nota.dataAutorizacao || nota.data || '').slice(0, 10);
const formatarData = (valor) => {
  if (!valor) return '—';
  const [ano, mes, dia] = valor.split('-');
  return `${dia}/${mes}/${ano}`;
};
const obterCidadeDestinatario = (nota = {}) => nota.cidadeDescarga
  ?? nota.CidadeDescarga
  ?? nota.cliente?.pessoa?.endereco?.cidade
  ?? nota.Cliente?.Pessoa?.Endereco?.Cidade
  ?? nota;
const formatarMunicipio = (nota) => {
  const cidade = obterCidadeDestinatario(nota);
  const nome = cidade.nome ?? cidade.Nome ?? cidade.municipioDescarga ?? cidade.MunicipioDescarga;
  const uf = cidade.uf ?? cidade.UF ?? cidade.ufDescarga ?? cidade.UFDescarga;
  const codigo = cidade.cMun ?? cidade.CMun ?? cidade.codigoMunicipio ?? cidade.CodigoMunicipio ?? cidade.codigoMunicipioDescarga ?? cidade.CodigoMunicipioDescarga;
  return nome && uf && codigo ? `${nome}/${uf} · ${codigo}` : 'Sem município';
};

const NFeAutorizadaSearch = ({ show, onHide, notas, onSelect }) => {
  const [filtros, setFiltros] = useState({ dataInicial: primeiroDiaMes(), dataFinal: ultimoDiaMes(), pesquisa: '' });

  useEffect(() => {
    if (show) {
      setFiltros({ dataInicial: primeiroDiaMes(), dataFinal: ultimoDiaMes(), pesquisa: '' });

    }
  }, [show]);

  const filtradas = useMemo(() => {
    const pesquisa = normalizeText(filtros.pesquisa);
    return notas.filter((nota) => {
      const dataNota = obterDataNota(nota);
      const dentroPeriodo = dataNota
        && (!filtros.dataInicial || dataNota >= filtros.dataInicial)
        && (!filtros.dataFinal || dataNota <= filtros.dataFinal);
      const texto = normalizeText(`${nota.serie || ''} ${nota.numDoc || ''} ${nota.chave || ''} ${nota.clienteNome || ''} ${formatarMunicipio(nota)}`);
      return dentroPeriodo && (!pesquisa || texto.includes(pesquisa));
    });
  }, [notas, filtros]);

  const alterarFiltro = (campo, valor) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));

  };

  return (
    <ModalContainer show={show} largura="1100px" zIndex={1060} variant="busca" onHide={onHide}>
      <ModalHeader title="Buscar NF-e autorizada" onHide={onHide} />
      <ModalBody>
        <div className="d-flex gap-2 align-items-end flex-wrap mb-3">
          <Input label="Data inicial" type="date" value={filtros.dataInicial} onChange={(e) => alterarFiltro('dataInicial', e.target.value)} size={145} />
          <Input label="Data final" type="date" value={filtros.dataFinal} onChange={(e) => alterarFiltro('dataFinal', e.target.value)} size={145} />
          <ModalSearchBar label="Número, chave, destinatário ou município" value={filtros.pesquisa} onChange={(e) => alterarFiltro('pesquisa', e.target.value)} size={390} />
        </div>
        <LazyLoading items={filtradas} resetKey={JSON.stringify([show, filtros])}>
        {({ containerRef, handleScroll, visibleItems }) => (
        <ModalTable containerRef={containerRef} onContainerScroll={handleScroll}>
          <thead><tr><th>Emissão</th><th>Série/Número</th><th>Chave</th><th>Destinatário</th><th>Município</th><th>Valor</th><th className="modal-select-column">Selecionar</th></tr></thead>
          <tbody>
            {visibleItems.map((nota) => (
              <tr key={nota.id} onDoubleClick={() => onSelect(nota)}>
                <td>{formatarData(obterDataNota(nota))}</td>
                <td>{nota.serie}/{nota.numDoc}</td>
                <td>{nota.chave}</td>
                <td>{nota.clienteNome || 'Sem destinatário'}</td>
                <td>{formatarMunicipio(nota)}</td>
                <td>{moeda(nota.vNota)}</td>
                <td className="modal-select-column"><ModalSelectButton type="button" className="btn btn-outline-primary btn-sm" onClick={() => onSelect(nota)} label={`NF-e ${nota.serie}/${nota.numDoc}`} /></td>
              </tr>
            ))}
            {!visibleItems.length && <tr><td colSpan="7" className="text-center">Nenhuma NF-e encontrada no período.</td></tr>}
          </tbody>
        </ModalTable>
        )}
        </LazyLoading>
      </ModalBody>
      <ModalFooter>

        <ModalActionButton type="button" className="btn btn-secondary" onClick={onHide} action="close">Fechar</ModalActionButton>
      </ModalFooter>
    </ModalContainer>
  );
};

export default NFeAutorizadaSearch;
